import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import GoogleReCaptcha from '../components/GoogleReCaptcha';

function Login({ onLogin }) {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [processingMagicLink, setProcessingMagicLink] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);

  // ✅ Handle 'beforeinstallprompt' event for PWA install
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstallable(false);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // ✅ Handle magic link redirect from Supabase
  useEffect(() => {
    const handleMagicLink = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        setProcessingMagicLink(true);
        try {
          const params = new URLSearchParams(hash.replace('#', ''));
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          
          if (accessToken) {
            const response = await fetch(`${API_URL}/auth/magic-link`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ supabase_token: accessToken })
            });
            
            const data = await response.json();
            
            if (data.success) {
              localStorage.setItem('token', data.token);
              if (data.supabase_token) {
                localStorage.setItem('supabase_token', data.supabase_token);
              }
              if (data.supabase_refresh_token) {
                localStorage.setItem('supabase_refresh_token', data.supabase_refresh_token);
              }
              localStorage.setItem('user', JSON.stringify(data.user));
              window.history.replaceState(null, '', window.location.pathname);
              
              if (data.needs_password) {
                toast.info('Welcome! Please set your password.');
                navigate('/set-password');
              } else {
                if (data.user.role === 'admin' || data.user.role === 'super_admin' || data.user.role === 'user') {
                  onLogin('admin');
                  navigate('/dashboard');
                } else if (data.user.role === 'client') {
                  onLogin('client');
                  navigate('/client-dashboard');
                } else if (data.user.role === 'subcontractor') {
                  onLogin('subbie');
                  navigate('/subbie-dashboard');
                }
              }
            } else {
              toast.error('Could not verify your login link. Please try again.');
            }
          }
        } catch (e) {
          console.error('Magic link error:', e);
          toast.error('Failed to process login link. Please try again.');
        } finally {
          setProcessingMagicLink(false);
        }
      }
    };
    
    handleMagicLink();
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      toast.success('✅ OneView has been installed to your desktop!');
      setIsInstallable(false);
    }
    setDeferredPrompt(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // ✅ Get reCAPTCHA v3 token (invisible - no user interaction)
      let token = recaptchaToken;
      if (!token && recaptchaRef.current) {
        token = await recaptchaRef.current.execute();
      }
      
      if (!token) {
        toast.error('Security verification failed. Please try again.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, recaptcha_token: token })
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('token', data.token);
        if (data.supabase_token) {
          localStorage.setItem('supabase_token', data.supabase_token);
        }
        if (data.supabase_refresh_token) {
          localStorage.setItem('supabase_refresh_token', data.supabase_refresh_token);
        }
        localStorage.setItem('user', JSON.stringify(data.user));

        if (data.user.role === 'admin' || data.user.role === 'super_admin' || data.user.role === 'user') {
          onLogin('admin');
          navigate('/dashboard');
        } else if (data.user.role === 'client') {
          onLogin('client');
          navigate('/client-dashboard');
        } else if (data.user.role === 'subcontractor') {
          onLogin('subbie');
          navigate('/subbie-dashboard');
        } else {
          toast.error('Unknown role: ' + data.user.role);
        }
      } else {
        toast.error(data.error || 'Invalid login. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="flex bg-white rounded-3xl overflow-hidden max-w-5xl w-full shadow-xl border border-gray-200">
        
        {/* Left side - Login Form */}
        <div className="flex-1 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="/Images/OneViewLogo clear background.png" 
              alt="OneView" 
              className="h-12 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = document.createElement('div');
                fallback.className = 'w-11 h-11 bg-navy rounded-xl flex items-center justify-center text-green font-extrabold text-xl';
                fallback.textContent = 'OV';
                e.target.parentNode.insertBefore(fallback, e.target);
              }}
            />
          </div>

          <h1 className="text-3xl font-bold text-navy mb-2">Welcome Back</h1>
          <p className="text-gray-500 mb-8">Log in to your OneView portal</p>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-navy mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-green focus:ring-2 focus:ring-green/10"
                placeholder="you@company.com"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-navy mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-green focus:ring-2 focus:ring-green/10"
                placeholder="••••••••"
                required
              />
            </div>

            {/* Invisible reCAPTCHA v3 - no visible widget */}
            <GoogleReCaptcha ref={recaptchaRef} onVerify={(token) => setRecaptchaToken(token)} />

            <div className="flex justify-end mb-6">
              <Link to="/forgot-password" className="text-sm text-green hover:underline">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-navy text-white rounded-xl font-semibold hover:bg-green hover:text-navy transition-colors disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Log In →'}
            </button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-sm text-gray-400">or</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          <div className="space-y-3">
            <Link
              to="/subbie-signup"
              className="w-full flex items-center justify-center gap-3 py-3 border-2 border-green/20 rounded-xl hover:bg-green/5 transition-colors"
            >
              <span className="text-xl">🚛</span>
              <div className="text-left">
                <p className="text-sm font-semibold text-navy">Become a Partner</p>
                <p className="text-xs text-gray-500">Sign up as a subcontractor</p>
              </div>
              <span className="text-green ml-auto">→</span>
            </Link>

            <Link
              to="/client-signup"
              className="w-full flex items-center justify-center gap-3 py-3 border-2 border-blue-200 rounded-xl hover:bg-blue-50 transition-colors"
            >
              <span className="text-xl">📊</span>
              <div className="text-left">
                <p className="text-sm font-semibold text-navy">Client Portal Access</p>
                <p className="text-xs text-gray-500">Register your company for OneView</p>
              </div>
              <span className="text-blue-500 ml-auto">→</span>
            </Link>

            {isInstallable && (
              <button
                onClick={handleInstallClick}
                className="w-full flex items-center justify-center gap-3 py-3 border-2 border-navy/20 rounded-xl hover:bg-navy/5 transition-colors"
              >
                <span className="text-xl">📥</span>
                <div className="text-left">
                  <p className="text-sm font-semibold text-navy">Download Desktop App</p>
                  <p className="text-xs text-gray-500">Install OneView on your computer</p>
                </div>
                <span className="text-navy ml-auto">↓</span>
              </button>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              Powered by <span className="text-green font-medium">One Purpose Logistics</span>
            </p>
          </div>
        </div>

        <div className="flex-1 bg-navy p-12 text-white hidden lg:flex flex-col justify-center">
          <h2 className="text-3xl font-bold mb-4">
            Bulk Transport.<br />
            <span className="text-green">Real-Time Intelligence.</span>
          </h2>
          <p className="text-gray-400 mb-8 leading-relaxed">
            OneView - Powered by One Purpose Logistics delivers heavy bulk across Africa. 
            Clients get live KPIs. Subcontractors get a simple portal. Everyone wins.
          </p>
          <ul className="space-y-3">
            <li className="flex items-center gap-3 text-gray-300">
              <span className="text-green text-xl">✓</span> Live KPI Dashboard
            </li>
            <li className="flex items-center gap-3 text-gray-300">
              <span className="text-green text-xl">✓</span> Real-Time Tracking
            </li>
            <li className="flex items-center gap-3 text-gray-300">
              <span className="text-green text-xl">✓</span> Subcontractor Management
            </li>
            <li className="flex items-center gap-3 text-gray-300">
              <span className="text-green text-xl">✓</span> Invoice & POD Management
            </li>
            <li className="flex items-center gap-3 text-gray-300">
              <span className="text-green text-xl">✓</span> Automated Reports
            </li>
          </ul>
          
          <div className="mt-10 pt-8 border-t border-white/10">
            <p className="text-xs text-gray-500">
              © 2026 One Purpose Logistics. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;